'use client';

import React, { useState } from 'react';
import { IRegionFeaturedResort, IRegionShort, IRelatedForRegion } from '@/api/region/types';
import { BottomMountains } from '@/components/blocks/BottomMountains';
import { Footer } from '@/components/blocks/Footer';
import { Container } from '@/components/ui/container';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { RegionHeaderNav } from '@/components/region/RegionHeaderNav';
import { CurrentLink, CurrentTab } from '@/components/region/RegionHeaderNav/constants';
import { Layout } from '@/components/resort/Layout/Layout';
import { AdUnitContext } from '@/tracking/ads/components/AdUnitContext';
import { TopAd } from '@/tracking/ads/components/TopAd';
import { I18nKey } from '@/i18n/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { IRegionBubblingProps, RegionLayoutContext } from '@/page-components/region/_context';
import { defaultImage } from '@/page-components/region/constants';
import { RegionFPCTracker } from '@/tracking/facebook/components/RegionFPCTracker';
import { TemplateProps } from '@/page-components/pathTypes';
import WeskiBottomPopupDiv from '@/components/common/WeskiBottomPopupDiv';

const REGION_TEMPLATE_NAMES_WITH_WESKI_BOTTOM_POPUP = new Set([
  'Region Open Resorts',
  'Region Projected Opening',
  'Region Projected Closing',
  'Region Lift Tickets',
  'Region Guide Page',
]);

export interface IRegionLayout extends TemplateProps {
  pathInfo: IRegionShort,
  featured: IRegionFeaturedResort,
  related: IRelatedForRegion,
  adTKey: string,
  meta: {
    title: I18nKey['seo'],
    description: I18nKey['seo'],
    redirectTo: string | null,
  },
  navigation: {
    head: HeaderNavCurLink,
    tab: CurrentTab,
    link: CurrentLink,
  },
  hero: {
    title: I18nKey['common'],
  }
  templateName: string,
}

export const RegionLayout = ({
  children,
  pathInfo,
  featured,
  adTKey,
  navigation,
  hero,
  related,
  templateName,
}: IRegionLayout & { children: React.ReactNode }) => {
  const { t } = useOTSTranslation('common');
  const [, setBubbleProps] = useState<IRegionBubblingProps>({});
  const shouldRenderBottomWeskiPopup = REGION_TEMPLATE_NAMES_WITH_WESKI_BOTTOM_POPUP.has(templateName);

  return (
    <RegionLayoutContext.Provider value={setBubbleProps}>
      <div style={{ overflowX: 'clip' }}>
        <AdUnitContext
          region={pathInfo}
          adType="region"
          adTKey={adTKey}
          meta={{
            Fs: undefined,
            Rs: undefined,
            Th: undefined,
            Tl: undefined,
            snow: undefined,
            Wc: undefined,
          }}
        >
          <RegionFPCTracker
            title={pathInfo.title}
            uuid={pathInfo.uuid}
          />
          <HeaderNav current={navigation.head} />
          <TopAd id={`region-${pathInfo.uuid}`} templateName={templateName} />
          <Container width="xl-only" className="content-container">
            <RegionHeaderNav
              photo={featured?.details?.image ?? defaultImage}
              resort={featured?.resort}
              link={navigation.link}
              region={pathInfo}
              tab={navigation.tab}
              related={related}
              title={t(hero.title, { region: pathInfo.title })}
            />
            {/* Skip the legacy .layout-container class (globals.scss):
                its mobile rule pulls content up by -1rem to overlap the
                old photo HeroNav, which now clips the v3 SubNav.
                Re-apply the lg+ 1rem vertical padding and the white
                background in Tailwind so those bits don't regress. */}
            <Layout className="bg-white lg:py-4">
              {children}
            </Layout>
          </Container>
          <BottomMountains />
          <Footer />
          {shouldRenderBottomWeskiPopup && (
            <WeskiBottomPopupDiv isRegion id={pathInfo.uuid} />
          )}
        </AdUnitContext>
      </div>
    </RegionLayoutContext.Provider>
  );
};
